<template>
  <footer class="app-footer">
    <div class="footer-content">
      <div class="container">
        <div class="footer-grid">
          <!-- Logo and Description -->
          <div class="footer-section">
            <div class="footer-logo">
              <AppLogo :small="true" :size="30" />
            </div>
            <p class="footer-description">
              {{ $t("common.footerDescription") }}
            </p>
          </div>

          <!-- Quick Links -->
          <div class="footer-section">
            <h4 class="footer-title">{{ $t("footer.quickLinks") }}</h4>
            <ul class="footer-links">
              <li>
                <router-link to="/">{{ $t("nav.home") }}</router-link>
              </li>
              <li>
                <router-link to="/courses">{{ $t("nav.courses") }}</router-link>
              </li>
              <li>
                <router-link to="/universities">{{
                  $t("nav.universities")
                }}</router-link>
              </li>
              <li>
                <router-link to="/bookmarks">{{ $t("nav.bookmarks") }}</router-link>
              </li>
            </ul>
          </div>

          <!-- Features -->
          <div class="footer-section">
            <h4 class="footer-title">{{ $t("footer.features") }}</h4>
            <ul class="footer-links">
              <li>
                <router-link to="/recommendations">{{
                  $t("nav.recommendations")
                }}</router-link>
              </li>
              <li>
                <router-link
                  :to="{ path: '/courses', query: { view: 'advanced' } }"
                  >{{ $t("footer.advancedSearch") }}</router-link
                >
              </li>
            </ul>
          </div>

          <!-- About & Contact -->
          <div class="footer-section">
            <h4 class="footer-title">{{ $t("footer.aboutContact") }}</h4>
            <ul class="footer-links">
              <li>
                <router-link to="/about">{{
                  $t("footer.aboutUs")
                }}</router-link>
              </li>
              <li>
                <a href="mailto:kurskompass.de@gmail.com">{{
                  $t("footer.contact")
                }}</a>
              </li>
              <li>
                <span class="feedback-link" @click="feedbackDialogVisible = true">{{
                  $t("footer.sendFeedback")
                }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Footer Bottom -->
        <div class="footer-bottom">
          <div class="footer-copyright">
            <p>&copy; 2025 KursKompass. {{ $t("footer.allRightsReserved") }}</p>
          </div>
          <div class="footer-language">
            <LanguageSelector />
          </div>
        </div>
      </div>
    </div>

    <!-- Feedback Dialog -->
    <FeedbackDialog v-model="feedbackDialogVisible" />
  </footer>
</template>

<script setup lang="ts">
import { ref } from "vue";
import AppLogo from "./AppLogo.vue";
import LanguageSelector from "./LanguageSelector.vue";
import FeedbackDialog from "./FeedbackDialog.vue";

const feedbackDialogVisible = ref(false);
</script>

<style scoped>
.app-footer {
  background: #2c3e50;
  color: white;
  margin-top: auto;
}

.footer-content {
  padding: 60px 0 20px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.footer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 40px;
}

.footer-section h4 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #ffd04b;
}

.footer-logo {
  margin-bottom: 15px;
}

.footer-description {
  color: #bdc3c7;
  line-height: 1.6;
  margin-bottom: 20px;
}

.footer-social {
  display: flex;
  gap: 15px;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
}

.social-link:hover {
  background: #667eea;
  transform: translateY(-2px);
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 12px;
}

.footer-links a,
.footer-links .feedback-link {
  color: #bdc3c7;
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;
}

.footer-links a:hover,
.footer-links .feedback-link:hover {
  color: #ffd04b;
}

.footer-bottom {
  border-top: 1px solid #34495e;
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.footer-copyright p {
  color: #bdc3c7;
  margin: 0;
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .footer-bottom {
    flex-direction: column;
    text-align: center;
  }
}
</style>
